import React from "react";
import { toast } from "sonner";
import { ChevronDown, Globe } from "lucide-react";
import { LK, HK, MY, TH, BD } from "country-flag-icons/react/3x2";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "./ui/dropdown-menu";

/**
 * FutureTec country switcher.
 * Payment model: single international gateway (Stripe / Paddle / Lemon Squeezy MoR)
 * — no per-country local rails needed for launch.
 * Selected country persists to LocalStorage `futuretec_country`.
 *
 * Flags are inline SVG (via country-flag-icons) rather than regional-indicator
 * emojis because Windows Chrome and stock Linux Chromium have no color-emoji
 * font by default and fall back to the underlying letter pairs (🇱🇰 → "LK").
 * SVG is deterministic across all platforms.
 */
const FLAGS = { LK, HK, MY, TH, BD };

export const COUNTRIES = [
  { code: "LK", name: "Sri Lanka" },
  { code: "HK", name: "Hong Kong" },
  { code: "MY", name: "Malaysia"  },
  { code: "TH", name: "Thailand"  },
  { code: "BD", name: "Bangladesh" },
];

const STORAGE_KEY = "futuretec_country";
const DEFAULT_CODE = "LK";

function readInitial() {
  if (typeof window === "undefined") return DEFAULT_CODE;
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved && COUNTRIES.some((c) => c.code === saved)) return saved;
  } catch (_) {
    /* localStorage unavailable — fall through to default */
  }
  return DEFAULT_CODE;
}

function FlagSvg({ code, title, size = 18 }) {
  const F = FLAGS[code];
  if (!F) return null;
  // 3:2 aspect ratio; keep width fixed and derive height so alignment stays clean.
  return (
    <F
      title={title}
      style={{ width: size, height: Math.round(size * 2 / 3), display: "inline-block", verticalAlign: "middle", borderRadius: 2, boxShadow: "0 0 0 1px rgba(43,42,38,0.10)" }}
    />
  );
}

export default function CountrySwitcher() {
  const [code, setCode] = React.useState(readInitial);
  const current = COUNTRIES.find((c) => c.code === code) || COUNTRIES[0];

  // Seed localStorage on first mount so downstream code can rely on it
  // being present even if the user never opens the dropdown.
  React.useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, code);
    } catch (_) { /* ignore */ }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const pick = (next) => {
    // NOTE: intentionally no early-return on `next === code`. Re-clicking
    // the currently-selected item MUST still fire a toast + re-emit the
    // event so the user gets visible feedback for every click.
    setCode(next);
    const picked = COUNTRIES.find((c) => c.code === next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
      window.dispatchEvent(
        new CustomEvent("futuretec:country-changed", { detail: { code: next } })
      );
    } catch (_) { /* ignore */ }
    if (picked) toast.success(`Country: ${picked.name}`);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="ftg-btn ftg-btn--ghost ftg-country-switcher"
          aria-label={`Country: ${current.name}`}
          data-testid="country-switcher-trigger"
        >
          <Globe size={14} aria-hidden="true" />
          <span className="ftg-country-switcher__flag" aria-hidden="true">
            <FlagSvg code={current.code} title={current.name} />
          </span>
          <span className="ftg-country-switcher__name">{current.name}</span>
          <ChevronDown size={14} aria-hidden="true" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" data-testid="country-switcher-menu">
        <DropdownMenuLabel>Choose your country</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuRadioGroup value={code}>
          {COUNTRIES.map((c) => (
            <DropdownMenuRadioItem
              key={c.code}
              value={c.code}
              onSelect={() => pick(c.code)}
              data-testid={`country-switcher-option-${c.code}`}
            >
              <span aria-hidden="true" style={{ marginRight: 8, display: "inline-flex", alignItems: "center" }}>
                <FlagSvg code={c.code} title={c.name} />
              </span>
              {c.name}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
